/**
 * The chrome around every screen, and the one control that leaves it.
 *
 * Decided 18 Sep 2026: ONE button, not two. Its label changes with where you
 * are. Outside a session it says Home and just goes. Inside one it says Leave
 * and asks first. On the summary it says Done.
 *
 * Two buttons that do the same thing make a child hesitate, and a hesitating
 * child taps the wrong one.
 */
import { useState } from 'react'
import { ExitButton } from './ExitButton'
import type { ReactNode } from 'react'
import { theme } from '../theme'
import { Button, Card } from './ui'
import { t } from '../lib/i18n'

export type ExitMode = 'home' | 'leave' | 'done' | 'none'

export function Shell({
  children, title, step, exitMode = 'home', onExit, right, grownUp = false,
}: {
  /** A grown-up's screen (Settings): "← Home" in the family's language. */
  grownUp?: boolean
  children: ReactNode
  /** A node rather than a string, so the provenance overlay can label parts of it. */
  title?: ReactNode
  /** "Meaning — 4 of 9". A child who looks up mid-session should not have to guess. */
  step?: ReactNode
  exitMode?: ExitMode
  onExit?: () => void
  right?: ReactNode
}) {
  const [confirming, setConfirming] = useState(false)

  const handleExit = () => {
    if (exitMode === 'leave') setConfirming(true)
    else onExit?.()
  }

  return (
    <>
      {title || step || right ? (
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginBottom: 18, minHeight: 32,
        }}>
          <div>
            {title ? (
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.text2 }}>{title}</div>
            ) : null}
            {step ? (
              <div style={{ fontSize: 14, color: theme.faint, marginTop: 2 }}>{step}</div>
            ) : null}
          </div>
          {right}
        </header>
      ) : null}

      {children}

      {/* Pinned top-left, the same place on every screen (ExitButton). */}
      {exitMode !== 'none' ? (
        <ExitButton onExit={handleExit}
          label={exitMode === 'leave' ? 'Leave' : exitMode === 'done' ? 'Done'
            : grownUp ? t('← Home') : '← Home'} />

      ) : null}

      {confirming ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(21,32,43,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, zIndex: 40,
          }}
        >
          <Card style={{ maxWidth: 380, width: '100%' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, color: theme.text }}>
              Stop for now?
            </h2>
            <p style={{ margin: '0 0 20px', color: theme.muted, fontSize: 16, lineHeight: 1.5 }}>
              Your work is saved. You can carry on from here later today.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button tone="ghost" onClick={() => setConfirming(false)}>Keep going</Button>
              <Button tone="child" onClick={() => { setConfirming(false); onExit?.() }}>
                Leave
              </Button>
            </div>
          </Card>
        </div>
      ) : null}
    </>
  )
}
